import { useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { normalizeAdsChunk } from '@/components/AdsWithInfiniteScroll/VipAndLatestAdsWithInfiniteScroll.helpers';
import { GetAdsResponse, ServiceName } from '@/api/types/ads.types';
import { Ad } from '@/api/types/common.types';
import { GET_ADS } from '@/api/queries/ads.queries';
import { mergeAdsResults } from '@/utils/helpers';

const EMPTY_ADS: Ad[] = [];

function buildInitialAdsResponse(ads: Ad[] | null): GetAdsResponse | undefined {
  if (!ads) return undefined;

  return {
    ads: {
      nodes: ads,
      pageInfo: {
        endCursor: '',
        hasNextPage: false,
        hasPreviousPage: false,
      },
    },
  } as GetAdsResponse;
}

interface UseAdsQueryOptions {
  serviceName: ServiceName;
  variables: Record<string, unknown>;
  chunkSize: AdsChunkSize;
  initialAds: Ad[] | null;
  skip: boolean;
}

export function useAdsQuery({
  serviceName,
  variables,
  chunkSize,
  initialAds,
  skip,
}: UseAdsQueryOptions) {
  const {
    data: queryData,
    loading,
    refetch,
    fetchMore,
    updateQuery,
  } = useQuery<GetAdsResponse>(GET_ADS(serviceName), {
    variables,
    notifyOnNetworkStatusChange: true,
    skip,
    onCompleted: completedData =>
      normalizeAdsChunk(completedData, chunkSize, updateQuery),
  });

  const hasHydratedRef = useRef(false);

  if (!loading && !skip && !hasHydratedRef.current) {
    hasHydratedRef.current = true;
  }

  const data =
    queryData ??
    (hasHydratedRef.current ? undefined : buildInitialAdsResponse(initialAds));

  const nodes = data?.ads?.nodes ?? EMPTY_ADS;
  const endCursor = data?.ads?.pageInfo?.endCursor;
  const hasNextPage = data?.ads?.pageInfo?.hasNextPage ?? false;
  const isLoading = loading && (hasHydratedRef.current || !initialAds);

  const refetchAndNormalize = useCallback(
    () =>
      refetch().then(({ data: refetchedData }) =>
        normalizeAdsChunk(refetchedData, chunkSize, updateQuery)
      ),
    [refetch, chunkSize, updateQuery]
  );

  const fetchMoreAds = useCallback(
    (after?: string) =>
      fetchMore({
        variables: after ? { after } : undefined,
        updateQuery: (prevResult, { fetchMoreResult }) =>
          mergeAdsResults(prevResult, fetchMoreResult, { chunkSize }),
      }),
    [fetchMore, chunkSize]
  );

  return {
    nodes,
    endCursor,
    hasNextPage,
    isLoading,
    refetchAndNormalize,
    fetchMoreAds,
  };
}
